import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Maximize2, Minimize2, Minus, Plus, ShoppingBag, Trash2, X } from 'lucide-react';
import { useCart } from './CartProvider';

type DrawerSize = 'normal' | 'minimized' | 'maximized';

/**
 * POS-style cart drawer — slides in from the **right** when an item is added
 * to the cart, or when the cart icon is clicked. Mirrors the live order
 * panel of a point-of-sale terminal: thumbnails + qty controls on top,
 * persistent subtotal + checkout footer on the bottom.
 *
 * Sizes:
 * - `normal` (default) — fixed-width side panel.
 * - `minimized` — collapsed to a compact 56-px strip showing only the
 *   item-count badge; click the badge or the maximize control to expand.
 * - `maximized` — fills the full viewport for a focused checkout mode.
 *
 * Mount once near the top of the app tree (inside `<CartProvider>` and a
 * router context). Visibility is driven by `useCart().drawerOpen`.
 */
export function CartDrawer() {
  const { items, count, subtotal, drawerOpen, closeDrawer, openDrawer, setQty, remove, clear } = useCart();
  const [size, setSize] = useState<DrawerSize>('normal');

  /* Close the drawer whenever the URL changes — otherwise the
     body-scroll lock below stays armed after navigating to Cart /
     Checkout via a non-drawer link, which manifests as "the page
     doesn't scroll" on the destination page. Bug ref: AI Agent —
     "Cart and checkout pages are not scrolling properly". */
  const location = useLocation();
  useEffect(() => {
    if (drawerOpen) closeDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Escape key closes the panel from anywhere — matches the modal
  // a11y pattern shoppers expect from native dialogs. Skipped while
  // the drawer is minimised to a strip so Escape inside an
  // unrelated input doesn't dismiss the strip unexpectedly.
  useEffect(() => {
    if (!drawerOpen || size === 'minimized') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setSize('normal');
        closeDrawer();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen, size, closeDrawer]);

  // Lock body scroll while the drawer (in modal modes) is on screen
  // — without this, the page behind the backdrop scrolls under the
  // user's finger / wheel, which feels broken on touch devices.
  // The minimised strip is non-modal so it doesn't trigger the lock.
  useEffect(() => {
    if (!drawerOpen || size === 'minimized') return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [drawerOpen, size]);

  if (!drawerOpen) return null;

  // Minimized: render only a vertical strip on the right edge.
  if (size === 'minimized') {
    return (
      <button
        type="button"
        onClick={() => { setSize('normal'); openDrawer(); }}
        aria-label="Expand cart"
        className="fixed right-0 top-1/2 z-[60] flex -translate-y-1/2 flex-col items-center gap-2 border border-r-0 border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-3 shadow-lg hover:bg-[var(--color-bg-card-hover)]"
      >
        <ShoppingBag className="h-4 w-4 text-[var(--color-text-primary)]" />
        {count > 0 ? (
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-primary)] px-1.5 text-[11px] font-bold text-[var(--color-primary-fg)]">
            {count}
          </span>
        ) : null}
        <Maximize2 className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
      </button>
    );
  }

  const panelWidth = size === 'maximized' ? '100%' : '380px';

  return (
    <div className="fixed inset-0 z-[60] flex">
      {/* Left backdrop — clicks dismiss (only when not maximized) */}
      {size !== 'maximized' ? (
        <button
          type="button"
          aria-label="Close cart"
          onClick={closeDrawer}
          className="flex-1 cursor-default bg-black/40"
        />
      ) : null}

      {/* Right panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Cart"
        style={{ width: panelWidth, maxWidth: '100vw' }}
        className="flex flex-col border-l border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
            <ShoppingBag className="h-4 w-4" />
            Current order
            <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-primary)] px-1.5 text-[11px] font-bold text-[var(--color-primary-fg)]">
              {count}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setSize('minimized')}
              aria-label="Minimize cart"
              title="Minimize"
              className="inline-flex h-7 w-7 items-center justify-center text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)] hover:text-[var(--color-text-primary)]"
            >
              <Minus className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setSize((s) => (s === 'maximized' ? 'normal' : 'maximized'))}
              aria-label={size === 'maximized' ? 'Restore cart' : 'Maximize cart'}
              title={size === 'maximized' ? 'Restore' : 'Maximize'}
              className="inline-flex h-7 w-7 items-center justify-center text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)] hover:text-[var(--color-text-primary)]"
            >
              {size === 'maximized' ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={() => { setSize('normal'); closeDrawer(); }}
              aria-label="Close cart"
              title="Close"
              className="inline-flex h-7 w-7 items-center justify-center text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)] hover:text-[var(--color-text-primary)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="grid place-items-center gap-3 px-4 py-10 text-center">
              <ShoppingBag className="h-10 w-10 text-[var(--color-text-muted)]" />
              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                Your cart is empty
              </p>
              <p className="max-w-[18rem] text-xs text-[var(--color-text-secondary)]">
                Add items from the catalog and they'll appear here for quick checkout.
              </p>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                <Link
                  to="/?c=electronics"
                  onClick={closeDrawer}
                  className="inline-flex items-center justify-center border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]"
                >
                  Electronics
                </Link>
                <Link
                  to="/?c=fashion"
                  onClick={closeDrawer}
                  className="inline-flex items-center justify-center border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]"
                >
                  Fashion
                </Link>
                <Link
                  to="/?c=home"
                  onClick={closeDrawer}
                  className="inline-flex items-center justify-center border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]"
                >
                  Home
                </Link>
                <Link
                  to="/"
                  onClick={closeDrawer}
                  className="inline-flex items-center justify-center bg-[var(--color-primary)] px-3 py-1.5 text-xs font-semibold text-[var(--color-primary-fg)] hover:bg-[var(--color-primary-hover)]"
                >
                  Browse all
                </Link>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {items.map((it) => (
                <li key={it.product.id} className="flex gap-3 px-4 py-3">
                  {/* Thumbnail + title both link to the product detail
                      page (`/shop/:id`). `decodeId` on the storefront
                      accepts a raw GUID so passing `it.product.id`
                      directly works without plumbing `encodeId` into
                      `@kss/ui`. Closing the drawer on click keeps the
                      PDP unobstructed. Bug ref: AI Agent 2026-05-26 —
                      "when click cart product link then product view
                      details should be showing still not showing". */}
                  <Link
                    to={`/shop/${it.product.id}`}
                    onClick={() => { setSize('normal'); closeDrawer(); }}
                    className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden border border-[var(--color-border)]"
                    style={{ background: it.product.imageBg ?? 'var(--color-bg-app)' }}
                  >
                    {it.product.image ? (
                      <img
                        src={it.product.image}
                        alt={it.product.title}
                        onError={(e) => {
                          /* Hide the broken-image icon and let the
                             parent's fallback gradient + brand mark
                             show through. Matches `CartPage`. */
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span
                        aria-label="Image unavailable"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[var(--color-primary)] text-xs font-bold text-[var(--color-primary-fg)]"
                      >
                        K
                      </span>
                    )}
                  </Link>
                  <div className="flex flex-1 flex-col gap-1">
                    <Link
                      to={`/shop/${it.product.id}`}
                      onClick={() => { setSize('normal'); closeDrawer(); }}
                      className="line-clamp-2 text-sm font-medium text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
                    >
                      {it.product.title}
                    </Link>
                    <p className="text-xs text-[var(--color-text-muted)]">
                      {it.product.currency ?? '₹'}
                      {it.product.price.toLocaleString()}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setQty(it.product.id, it.qty - 1)}
                        aria-label="Decrease quantity"
                        className="inline-flex h-6 w-6 items-center justify-center border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="min-w-[1.5rem] text-center text-sm font-semibold text-[var(--color-text-primary)]">
                        {it.qty}
                      </span>
                      <button
                        type="button"
                        onClick={() => setQty(it.product.id, it.qty + 1)}
                        aria-label="Increase quantity"
                        className="inline-flex h-6 w-6 items-center justify-center border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(it.product.id)}
                        aria-label="Remove item"
                        className="ml-auto inline-flex h-6 w-6 items-center justify-center text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="self-start text-sm font-semibold text-[var(--color-text-primary)]">
                    {it.product.currency ?? '₹'}
                    {(it.product.price * it.qty).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="border-t border-[var(--color-border)] px-4 py-3">
          <div className="flex items-center justify-between text-sm font-semibold text-[var(--color-text-primary)]">
            <span>Subtotal</span>
            <span>₹ {subtotal.toLocaleString()}</span>
          </div>
          <p className="mt-0.5 text-[11px] text-[var(--color-text-muted)]">
            Taxes & shipping calculated at checkout.
          </p>
          <div className="mt-3 grid gap-2">
            <Link
              to="/cart"
              onClick={closeDrawer}
              className="inline-flex h-9 items-center justify-center bg-[var(--color-primary)] px-3 text-sm font-semibold text-[var(--color-primary-fg)] hover:bg-[var(--color-primary-hover)]"
            >
              Checkout
            </Link>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={closeDrawer}
                className="flex-1 border border-[var(--color-border)] px-3 py-1.5 text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]"
              >
                Continue shopping
              </button>
              {items.length > 0 ? (
                <button
                  type="button"
                  onClick={clear}
                  className="border border-[var(--color-border)] px-3 py-1.5 text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]"
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        </footer>
      </aside>
    </div>
  );
}
